<template>
  <div class="w-100 m-auto shipments-list">
    <ShipmentsSearchForm />

    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :server-items-length="shipmentsCount"
      :loading="loading"
      :headers="headers"
      :items="shipments"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #header-shipmentNumber="header">
        <div class="customize-header">
          {{ header.text }}
        </div>
      </template>
      <template #expand="item">
        <div style="padding: 15px">
          <ShipmentDetails :shipment="item" />
        </div>
      </template>
      <template #no-data> - </template>
      <template #item="{ column, item }: { column: string; item: Shipment }">
        <span v-if="column === 'createdAt'">
          <RowDate :date="item.createdAt" />
        </span>
        <span v-if="column === 'receivedInFacilityAt'">
          <RowDate :date="item.receivedInFacilityAt || ''" />
        </span>
        <span v-if="column === 'lastTrackingEventTime'">
          <RowDate :date="item.lastTrackingEventTime" />
        </span>
        <span v-if="column === 'user'">
          {{ item.company.name }}
        </span>
        <span v-if="column === 'recipient'">
          <RecipientCell :shipment="item" />
        </span>
        <span v-if="column === 'proxy'">
          <ProxyCell :shipment="item" />
        </span>
        <span v-if="column === 'shipmentNumber'">
          <ShipmentNumberCell :shipment="item" :display-announcement-status="true" />
        </span>
        <span v-if="column === 'lastTrackingEventStatus'">
          <TrackingStatusCell :shipment="item" />
        </span>
        <span v-if="column === 'carrierHandle'">
          <CarrierData :shipment="item" :only-icon="!!item.adjustedCarrierHandle" />
        </span>
      </template>
    </EasyDataTable>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="shipmentsCount"
    />
  </div>
</template>

<script lang="ts">
import type { Shipment } from '@/types/Models/Shipment'
import { BSpinner } from 'bootstrap-vue-next'
import { ref, computed, watch, onMounted, defineComponent, type Ref, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { makeRequestParams } from '@/services/Shipments'
import { useAccountStore } from '@/store/account'
import { useShipmentStore } from '@/store/shipment'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import ProxyCell from '@/views/Components/Shipments/Cells/ProxyCell.vue'
import CarrierData from '@/views/Components/Carrier/CarrierData.vue'
import RecipientCell from '@/views/Components/Shipments/Cells/Recipient.vue'
import ShipmentDetails from '@/views/Components/Shipments/Details/Details.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import ShipmentNumberCell from '@/views/Components/Elements/Table/ShipmentNumber.vue'
import TrackingStatusCell from '@/views/Components/Elements/Table/TrackingStatus.vue'
import ShipmentsSearchForm from '@/views/Components/Shipments/ShipmentsSearchForm.vue'

export default defineComponent({
  name: 'ShipmentsFeed',
  components: {
    BSpinner,
    CarrierData,
    ProxyCell,
    TablePagination,
    ShipmentNumberCell,
    TrackingStatusCell,
    ShipmentsSearchForm,
    ShipmentDetails,
    RecipientCell,
    RowDate,
    EasyDataTable,
  },
  setup() {
    const accountStore = useAccountStore()
    const shipmentStore = useShipmentStore()
    const route = useRoute()

    const dataTable = ref()
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const loading: ComputedRef<boolean> = computed(() => shipmentStore.shipmentsLoading)
    const shipments: ComputedRef<Shipment[]> = computed(() => shipmentStore.shipmentsList())
    const shipmentsCount: ComputedRef<number> = computed(() => shipmentStore.shipmentsTotal())

    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 50,
      sortType: 'desc',
      sortBy: 'createdAt',
    })
    const headers = computed<Header[]>(() => [
      { text: 'Shipment number', value: 'shipmentNumber' },
      { text: 'Created', value: 'createdAt', sortable: true },
      ...(isStaff.value ? [{ text: 'Scan', value: 'receivedInFacilityAt', sortable: true }] : []),
      { text: 'Tracking', value: 'lastTrackingEventTime', sortable: true },
      { text: 'Carrier', value: 'carrierHandle' },
      { text: 'Status', value: 'lastTrackingEventStatus' },
      ...(isStaff.value ? [{ text: 'Customer', value: 'user' }] : []),
      { text: 'Recipient', value: 'recipient' },
      ...(isStaff.value ? [{ text: 'Proxy', value: 'proxy' }] : []),
    ])

    const loadFromServer = () => {
      shipmentStore.fetchShipments(makeRequestParams({ route, serverOptions }))
    }

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }

    onMounted(loadFromServer)

    watch(serverOptions, loadFromServer, { deep: true })

    return {
      shipments,
      loading,
      headers,
      dataTable,
      serverOptions,
      shipmentsCount,
      updateServerOptions,
    }
  },
})
</script>
